import { decimalCompare, type DecimalValue } from "../values/decimal";
import { canCreateTransactionType } from "../platform/access-control";
import { TRANSACTION_TYPE_METADATA } from "./transaction-types";
import { dateOnlyKey, parseDateOnly } from "../values/date-only";
import { INITIAL_STOCK_COPY } from "./inventory-copy";

export const INITIAL_STOCK_TYPES = {
  openingBalance: "OPENING_BALANCE",
  purchase: "PURCHASE",
} as const;
export type InitialStockType = (typeof INITIAL_STOCK_TYPES)[keyof typeof INITIAL_STOCK_TYPES];
export const INITIAL_STOCK_TYPE_VALUES = [INITIAL_STOCK_TYPES.openingBalance, INITIAL_STOCK_TYPES.purchase] as const;
export const DEFAULT_INITIAL_STOCK_TYPE = INITIAL_STOCK_TYPES.openingBalance;

export const INITIAL_STOCK_MESSAGES = INITIAL_STOCK_COPY;

export function getInitialStockOptions(role: string | null | undefined) {
  return INITIAL_STOCK_TYPE_VALUES.filter((type) => canCreateTransactionType(role, type))
    .map((value) => ({ value, label: TRANSACTION_TYPE_METADATA[value].label }));
}

export function parseInitialPurchaseDate(value: string | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  return parseDateOnly(value);
}

type InitialStockInput = {
  initialAmount: DecimalValue;
  initialTransactionType?: InitialStockType;
  initialTransactionDate?: string;
  openingBalance?: { supplierContactId?: number | null };
};

type InitialStockIssue = {
  field: "openingBalance.supplierContactId" | "initialTransactionDate";
  message: string;
};

export function purchaseDateIssue(value: string | undefined, now = new Date()): string | null {
  if (!value) return INITIAL_STOCK_MESSAGES.dateRequired;
  const date = parseInitialPurchaseDate(value.slice(0, 10));
  if (!date) return INITIAL_STOCK_MESSAGES.dateInvalid;
  return dateOnlyKey(date) > dateOnlyKey(now) ? INITIAL_STOCK_MESSAGES.dateFuture : null;
}

export function initialStockRequiredFields(type: InitialStockType | undefined): InitialStockIssue["field"][] {
  if (type !== INITIAL_STOCK_TYPES.purchase) return [];
  const fields: InitialStockIssue["field"][] = [];
  if (TRANSACTION_TYPE_METADATA[type].supplierMode === "required") fields.push("openingBalance.supplierContactId");
  fields.push("initialTransactionDate");
  return fields;
}

export function initialStockIssues(input: InitialStockInput, now = new Date()): InitialStockIssue[] {
  if (decimalCompare(input.initialAmount, 0) <= 0 || input.initialTransactionType !== INITIAL_STOCK_TYPES.purchase) return [];
  const issues: InitialStockIssue[] = [];
  const required = initialStockRequiredFields(input.initialTransactionType);
  if (required.includes("openingBalance.supplierContactId") && !input.openingBalance?.supplierContactId) {
    issues.push({ field: "openingBalance.supplierContactId", message: INITIAL_STOCK_MESSAGES.supplierRequired });
  }
  const message = purchaseDateIssue(input.initialTransactionDate, now);
  if (message) issues.push({ field: "initialTransactionDate", message });
  return issues;
}
